import React from 'react';
import { useTranslation } from 'react-i18next';

import { CFG_ANIM } from '../../../../theme/configurator-tokens';
import { asset } from '../../lib/asset';
import { isResizedMessage, previewOrigin } from '../../lib/iframePreview';
import { PreviewPlaceholder } from '../wizard/PreviewPlaceholder';
import {
  LAPTOP_FRAME_DEFAULT,
  PHONE_FRAME_DEFAULT,
  PHONE_RADIUS_RATIO,
  PHONE_STRIP_H,
} from '../../steps/finish/components/DevicePreview/frame-art';
import type { DeviceMode } from '../wizard/DeviceSwitcher';
import {
  MorphRoot,
  MorphScreen,
  MorphTopBar,
  MorphBottomBar,
  BezelImg,
  FrameContent,
  ZoomLayer,
  FillIframe,
} from './PreviewStage.styles';

/** Desktop virtual render width (matches MacDevice). */
const RENDER_W = 1280;
/** Mobile virtual render width: the site lays out as if the viewport were this wide,
 *  then scales to fit the phone bezel's screen (this device is now the sole owner of
 *  that number; PreviewPane no longer keeps its own copy). */
const PHONE_VIEWPORT_W = 420;
/** Chrome-strip heights (% of the screen), per device. The phone's come from the shared
 *  frame art; the laptop shows only a browser-chrome strip and no bottom strip. */
const TOP_H = { mobile: PHONE_STRIP_H.top, desktop: '6%' } as const;
const BOTTOM_H = { mobile: PHONE_STRIP_H.bottom, desktop: '0' } as const;

/**
 * Keep an opaque cover up after each `device` change (never on first mount), so the
 * cross-origin site's re-layout at the new width happens hidden. Lifted the moment the
 * framed site announces `{ type: 'page-resized' }` — the DEDICATED resize signal (distinct
 * from the initial-load `page-rendered` that {@link useIframeReveal} gates on), which the
 * site emits once its reflow at the new width has SETTLED, so the cover lifts onto a
 * finished layout, never a still-stretching one. Reflow completion is unobservable across
 * origins, so only that message is trustworthy; {@link CFG_ANIM.RESIZE_COVER_MS} is a
 * generous backstop for a site that never answers, kept above a typical reflow so the
 * message wins in the normal case.
 *
 * The cover is raised DURING RENDER, not from an effect: it has to be part of the very
 * commit that rewrites the iframe's width, or the browser paints the resized preview
 * for a frame before the cover lands and the reflow flashes through. Setting state in
 * render re-renders before that paint. (The paired {@link PreviewPlaceholder} also has
 * no fade-IN, for the same reason.)
 */
function useResizeCover(
  device: DeviceMode,
  src: string,
  iframeRef: React.RefObject<HTMLIFrameElement>,
): boolean {
  const [prevDevice, setPrevDevice] = React.useState(device);
  const [covering, setCovering] = React.useState(false);

  if (prevDevice !== device) {
    setPrevDevice(device);
    setCovering(true);
  }

  React.useEffect(() => {
    if (!covering) return undefined;
    // Re-runs (restarting the window) when a further switch lands mid-cover.
    const timer = window.setTimeout(() => setCovering(false), CFG_ANIM.RESIZE_COVER_MS);

    const origin = previewOrigin(src);
    const onMessage = (event: MessageEvent<unknown>): void => {
      // Same trust check as useIframeReveal: only THIS iframe's own window, on the
      // origin we pointed it at, saying the one thing we asked it to say (here: resized).
      if (origin === null) return;
      if (event.origin !== origin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isResizedMessage(event.data)) return;
      window.clearTimeout(timer);
      setCovering(false);
    };
    window.addEventListener('message', onMessage);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
    };
  }, [covering, prevDevice, src, iframeRef]);

  return covering;
}

export type MorphDeviceProps = {
  /** Live site URL (the stable iframe's only `src`; reloads ONLY when this changes). */
  src: string;
  /** Which bezel + virtual width to show; the ONLY thing a Mobile/Desktop switch changes. */
  device: DeviceMode;
  /** Top-nav / status strip + screen base tint (selected colour). */
  topColor?: string;
  /** Home-indicator strip tint (phone only; selected colour). */
  bottomColor?: string;
  /** Brand body background (the flat colour the loading placeholder fills with). */
  bgColor?: string;
  /** Host-owned reveal gate (load + settle); keyed on previewUrl only (no re-gate on switch). */
  shown: boolean;
  onLoad: () => void;
  /** The SAME ref backing `shown`'s `page-rendered` gate: it identifies this iframe's
   *  own window, so that gate (and the resize-cover's own message listener, which reads
   *  this same ref) can tell this iframe's announcement apart from the mac/phone role
   *  iframes' (same origin, same URL). */
  iframeRef: React.RefObject<HTMLIFrameElement>;
  /** Bare = no bezel: the screen fills the wrapper and the site renders at the
   *  wrapper's REAL width (no virtual-width scaling), same as Mac/PhoneDevice's own
   *  `bare` — the full-screen Live Preview, which floats this same warm device
   *  instead of a separate role iframe (see PreviewOverlay). */
  bare?: boolean;
};

/**
 * The single-mode unified preview device. ONE warm iframe wrapped by a bezel that
 * morphs between the MacBook (desktop, rendered at 1280) and the phone (mobile,
 * rendered at 420, with real status/home chrome strips) as `device` changes. The
 * iframe element is authored once and never re-parented, so a device switch is pure
 * CSS/props: the previewed site reflows to its responsive layout at the new width
 * while its route + JS state (contentWindow) survive.
 *
 * The virtual width + scale live on the {@link ZoomLayer} wrapper, never on the iframe
 * (which stays a plain 100% x 100% {@link FillIframe}) — same structure as PhoneDevice.
 *
 * To spare the user the visible reflow, a switch drops the loading cover over the
 * SCREEN for a short settle window (see {@link useResizeCover}); the bezel morph
 * around it stays visible, so the toggle reads as a crisp frame change rather than a
 * page rearranging itself.
 */
export const MorphDevice = ({
  src,
  device,
  topColor,
  bottomColor,
  bgColor,
  shown,
  onLoad,
  iframeRef,
  bare = false,
}: MorphDeviceProps): React.ReactElement => {
  const { t } = useTranslation();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [rootW, setRootW] = React.useState(0);
  const [dims, setDims] = React.useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Measure with getBoundingClientRect (fractional), NOT offsetWidth (rounded to an int),
  // like MacDevice does. `scale` is derived from this, so an int would scale the 1280px
  // document to a width up to half a pixel off the screen cut-out: rounded up it pushes
  // the site's right edge under the clip, rounded down it leaves a hairline of screen
  // background. Only the enclosing SwitchSlide/StepFade transform this subtree, and only
  // by a translation, so the rect's width/height are the untransformed layout box.
  const measure = React.useCallback((): void => {
    const root = rootRef.current;
    const content = contentRef.current;
    if (root) {
      const w = root.getBoundingClientRect().width;
      setRootW((prev) => (prev === w ? prev : w));
    }
    if (content) {
      const r = content.getBoundingClientRect();
      setDims((d) => (d.w === r.width && d.h === r.height ? d : { w: r.width, h: r.height }));
    }
  }, []);

  // Measure BEFORE paint, on every commit. `dims` and `device` must always come from the
  // same commit: `virtualWidth` flips the instant `device` does, so a frame of stale
  // `dims` means `scale = oldWidth / 1280` (~0.19 on mobile -> desktop) and therefore a
  // `height` of `dims.h / scale` — a ~2800px-tall iframe. The framed site would lay out
  // at that height and lazily fetch a screen's worth of content we then throw away.
  // A ResizeObserver cannot close this: it reports a frame late, by which time we have
  // already handed the wrong box to the cross-origin document. The setState guards above
  // make this idempotent, so the extra pre-paint render converges immediately.
  React.useLayoutEffect(measure);

  // Still observe the root for the changes React does not commit: the pane resizing, the
  // host's settle burst repositioning the stage box.
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [measure]);

  const covering = useResizeCover(device, src, iframeRef);
  const isMobile = device === 'mobile';

  const screen = isMobile ? PHONE_FRAME_DEFAULT.screen : LAPTOP_FRAME_DEFAULT.screen;
  // Bare (full-screen): render the site at its REAL width, 1:1, like Mac/PhoneDevice's
  // own bare mode — no virtual-width scaling down from the build-step's fixed layout.
  const virtualWidth = bare ? dims.w || 1 : isMobile ? PHONE_VIEWPORT_W : RENDER_W;
  const scale = dims.w > 0 ? dims.w / virtualWidth : 0;
  const zoomHeight = scale > 0 ? `${dims.h / scale}px` : '100%';
  const radius = !bare && isMobile ? rootW * PHONE_RADIUS_RATIO : 0;

  // The cover stays up until the iframe is loaded AND (after a switch) the reflow
  // settle window has elapsed. The iframe keeps rendering underneath either way.
  const revealed = shown && !covering;

  return (
    <MorphRoot ref={rootRef}>
      <MorphScreen
        $bg={topColor}
        $left={screen.left}
        $top={screen.top}
        $width={screen.width}
        $height={screen.height}
        $radius={radius}
        $bare={bare}
      >
        <MorphTopBar $color={topColor} $h={isMobile ? TOP_H.mobile : TOP_H.desktop} $bare={bare} />
        <FrameContent ref={contentRef}>
          <ZoomLayer $width={virtualWidth} $height={zoomHeight} $scale={scale || 0.0001}>
            <FillIframe
              ref={iframeRef}
              title={isMobile ? t('Mobile preview') : t('Desktop preview')}
              src={src || undefined}
              onLoad={onLoad}
              data-cfg-preview="morph"
              $shown={revealed && scale > 0}
            />
          </ZoomLayer>
        </FrameContent>
        <MorphBottomBar
          $color={revealed ? bottomColor : topColor}
          $h={isMobile ? BOTTOM_H.mobile : BOTTOM_H.desktop}
          $bare={bare}
        />
        <PreviewPlaceholder shown={!revealed} bgColor={bgColor} />
      </MorphScreen>
      <BezelImg src={asset(LAPTOP_FRAME_DEFAULT.src)} alt="" $shown={!bare && !isMobile} />
      <BezelImg src={asset(PHONE_FRAME_DEFAULT.src)} alt="" $shown={!bare && isMobile} />
    </MorphRoot>
  );
};

export type DeviceGhostProps = {
  /** The device being switched AWAY from — the bezel + chrome this ghost wears. */
  device: DeviceMode;
  topColor?: string;
  bottomColor?: string;
  bgColor?: string;
};

/**
 * The outgoing half of a Mobile / Desktop switch: the old device's bezel over a flat
 * brand-coloured screen, with no iframe. It exists because the real device owns the one
 * warm iframe and cannot be in two places at once — so the host mounts this ghost at the
 * OLD anchor rect and slides it out to its park side while the real (morphed) device
 * slides in from the other, reproducing the two-device cross-slide exactly.
 *
 * Its screen is a flat fill rather than the old page: the previewed site has already
 * reflowed to the new width by the time this paints, so there is no "old" render left to
 * show. That is the same opaque cover the incoming device wears while it settles.
 */
export const DeviceGhost = ({ device, topColor, bottomColor, bgColor }: DeviceGhostProps): React.ReactElement => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [rootW, setRootW] = React.useState(0);

  // Mounted at a fixed rect for its whole (short) life, so one measure is enough — but it
  // must land BEFORE paint, or a phone ghost shows square screen corners for one frame.
  React.useLayoutEffect(() => {
    if (rootRef.current) setRootW(rootRef.current.getBoundingClientRect().width);
  }, []);

  const isMobile = device === 'mobile';
  const screen = isMobile ? PHONE_FRAME_DEFAULT.screen : LAPTOP_FRAME_DEFAULT.screen;

  return (
    <MorphRoot ref={rootRef}>
      <MorphScreen
        $bg={bgColor}
        $left={screen.left}
        $top={screen.top}
        $width={screen.width}
        $height={screen.height}
        $radius={isMobile ? rootW * PHONE_RADIUS_RATIO : 0}
        $bare={false}
      >
        <MorphTopBar $color={topColor} $h={isMobile ? TOP_H.mobile : TOP_H.desktop} $bare={false} />
        <FrameContent />
        <MorphBottomBar $color={bottomColor} $h={isMobile ? BOTTOM_H.mobile : BOTTOM_H.desktop} $bare={false} />
      </MorphScreen>
      <BezelImg src={asset(isMobile ? PHONE_FRAME_DEFAULT.src : LAPTOP_FRAME_DEFAULT.src)} alt="" $shown />
    </MorphRoot>
  );
};

export default MorphDevice;
